import {
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline'
import { Venue } from 'types/model'

import { H1 } from '@elements/headings'
import { SeeMore } from '@elements/see-more'
import { FormattedContent } from '@elements/formatted-content'

import { VenueSocialCard } from './venue-social-card'
import { VenueContactCard } from './venue-contact-card'
import { useGoogleMapsLink } from '@hooks/google-maps-link'
import { formatPhoneNumber } from '@utils/strings'
import { Gallery } from '@components/gallery'
import { formatAddress } from '@utils/address'

export function VenueAboutSection({ venue }: { venue: Venue }) {
  const mapsLink = useGoogleMapsLink(venue.location.address)

  return (
    <div className='px-3 py-6 border-b shadow-sm sm:p-6 lg:px-8 lg:py-10 bg-card border-standard-light'>
      <div className='max-w-6xl pb-6 mx-auto space-y-6'>
        <H1 className='heading-xl sm:heading-2xl'>About {venue.name}</H1>
        <Gallery
          images={venue.images}
          imageClassName='min-w-[294px]'
          alt={venue.name}
          w='294'
          h='196'
        />
        <SeeMore clamp={4} lineHeight={24}>
          <FormattedContent>{venue.details.description}</FormattedContent>
        </SeeMore>
        <div className='flex flex-col space-y-6 md:space-y-0 md:space-x-3 md:flex-row md:justify-center md:h-max'>
          {venue.location.address && mapsLink && (
            <VenueContactCard
              heading='Address'
              ctaLabel='Get Directions'
              ctaIcon={MapPinIcon}
              href={mapsLink}
            >
              <div className='flex flex-col space-y-1'>
                <div className='flex items-center justify-start -ml-1 space-x-1'>
                  <MapPinIcon className='w-4 h-4 shrink-0' />
                  <span className='font-bold'>{venue.name}</span>
                </div>
                <span>{formatAddress(venue.location.address)}</span>
              </div>
            </VenueContactCard>
          )}
          {venue.email && (
            <VenueContactCard
              heading='Email'
              href={`mailto:${venue.email}`}
              ctaLabel='Send'
              ctaIcon={EnvelopeIcon}
            >
              <div className='flex items-center justify-center space-x-2'>
                <EnvelopeIcon className='w-4 h-4 shrink-0' />
                <span className='font-bold'>{venue.email}</span>
              </div>
            </VenueContactCard>
          )}
          {venue.phone && (
            <VenueContactCard
              heading='Phone'
              href={`tel:${venue.phone}`}
              ctaLabel='Call'
              ctaIcon={PhoneIcon}
            >
              <div className='flex items-center justify-center space-x-2'>
                <PhoneIcon className='w-4 h-4 shrink-0' />
                <span className='font-bold'>
                  {formatPhoneNumber(venue.phone)}
                </span>
              </div>
            </VenueContactCard>
          )}
        </div>
        <div className='flex flex-col space-y-3 md:space-y-0 md:space-x-3 md:flex-row md:items-center md:justify-center md:h-max'>
          <VenueSocialCard venue={venue} type='twitter' />
          <VenueSocialCard venue={venue} type='instagram' />
          <VenueSocialCard venue={venue} type='facebook' />
          <VenueSocialCard venue={venue} type='tiktok' />
          <VenueSocialCard venue={venue} type='website' />
        </div>
      </div>
    </div>
  )
}
